import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";
import Underline from "@tiptap/extension-underline";
import TableRow from "@tiptap/extension-table-row";
import { TableControls } from "../extensions/TableControls";
import { CroppedImage, Highlight } from "../extensions/cropped-image";
import { CommentMark } from "../extensions/CommentMark";
import { PersistentCommentMark } from "../extensions/PersistentCommentMark";
import { SelectionHighlightMark } from "../extensions/SelectionHighlightMark";
import { DiffBlock } from "../extensions/DiffBlock";
import { DiffInline } from "../extensions/DiffInline";
import { Extension, type AnyExtension } from "@tiptap/core";
import { CustomTableCell } from "../extensions/CustomTableCell";
import { CustomTableHeader } from "../extensions/CustomTableHeader";
import { CustomTable } from "../extensions/CustomTable";
import { ScrollableTableView } from "../extensions/ScrollableTableView";
import { ChartBlock } from "../extensions/ChartBlock";

// CustomTable extension is responsible for colwidth parameter
// which saves the width of column and is used for pdf generation
// colwidth is an array of widths
// (not a number because theoretically cell can have colspan > 1)
// tiptap-markdown marks a list "loose" (blank line between items when serialized) whenever an
// <li> contains a <p> — which is always true for generated/loaded chapter content. That is what
// re-introduces vertical spacing between list items every time the editor saves. Force the tight
// attribute on so getMarkdown() always emits tight lists.
const ForceTightLists = Extension.create({
  name: "forceTightLists",
  addGlobalAttributes() {
    return [
      {
        types: ["bulletList", "orderedList"],
        attributes: {
          tight: {
            default: true,
            parseHTML: () => true,
            renderHTML: () => ({ "data-tight": "true" })
          }
        }
      }
    ];
  }
});

const baseExtensions = [
  StarterKit.configure({
    heading: { levels: [2, 3, 4] }
  }),
  Underline,
  Markdown.configure({
    html: true, // Allow HTML in markdown
    tightLists: false, // Don't force tight lists
    bulletListMarker: "-",
    transformPastedText: false, // Don't transform pasted text
    transformCopiedText: false, // Don't transform copied text
    breaks: false // Don't automatically convert line breaks
  }),
  Link.configure({
    openOnClick: true,
    HTMLAttributes: {
      class: "underline text-blue-500 hover:text-blue-600 cursor-pointer"
    }
  }),
  CroppedImage.configure({
    inline: true,
    allowBase64: true,
    HTMLAttributes: {
      class: "max-w-full h-auto rounded-md"
    }
  }),
  CustomTable.configure({
    resizable: true,
    View: ScrollableTableView,
    HTMLAttributes: {
      class: "border-collapse w-full"
    }
  }),
  TableRow.configure({
    HTMLAttributes: {
      class: "border-b border-gray-200"
    }
  }),
  CustomTableHeader.configure({
    HTMLAttributes: {
      class:
        "border-b-2 border-gray-300 bg-gray-100 font-semibold text-left p-2 break-words min-w-0"
    }
  }),
  CustomTableCell.configure({
    HTMLAttributes: {
      class: "border border-gray-200 p-2 break-words min-w-0"
    }
  }),
  Highlight,
  Placeholder.configure({
    placeholder: "Type your memo here…"
  }),
  ForceTightLists
];

export const createEditorExtensions = (list?: AnyExtension[]) => [
  ...baseExtensions,
  TableControls,
  CommentMark,
  PersistentCommentMark,
  SelectionHighlightMark,
  ChartBlock,
  ...(list ?? [])
];

export const createEditorExtensionsWithDiff = (list?: AnyExtension[]) => [
  ...baseExtensions,
  DiffBlock,
  DiffInline,
  ChartBlock,
  ...(list ?? [])
];
